import { PriceRangeValue } from 'components/RangeFilterWrapper/RangeFilterWrapper.component';
import { Option } from 'models/Global';
import React, { useCallback } from 'react';
import PriceRangeFilter from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/PriceRangeFilter';
import SearchableFilter from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioSellerFilters/componentes/SearchableFilter';
import SearchableMultiSelectFilter from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioSellerFilters/componentes/SearchableMultiSelectFilter';
import useMatchMedia from './useMatchMedia';
import { useTranslation } from 'react-i18next';
import utils from 'utils';
import {
  HandleChangeSearchableFilter,
  SearchableFilterValue,
} from 'models/Filters';

const useGetFilterOptions = () => {
  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const getSearchableFilterApply = useCallback(
    (filterKey: string, filterName: string) =>
      (option: SearchableFilterValue) => {
        if (Array.isArray(option)) {
          const ids = option.map(({ value }) => value);
          const names = option.map(({ label }) => label);

          return {
            [filterKey]: utils.mapArrayToParams(ids),
            [filterName]: utils.mapArrayToParams(names),
          };
        }

        return {
          [filterKey]: option?.value,
          [filterName]: option?.label,
        };
      },
    [],
  );

  const getSearchableFilterInitialValue = useCallback(
    (filterKey: string, filterName: string, isMultiple = false) =>
      (activeFilter: Record<string, string>) => {
        if (isMultiple) {
          const ids = utils.mapParamsToArray(activeFilter[filterKey]);
          const names = utils.mapParamsToArray(activeFilter[filterName] || '');

          return ids.map((value, index) => ({
            value,
            label: names[index] || '',
          }));
        }

        return {
          value: activeFilter[filterKey],
          label: activeFilter[filterName] || '',
        };
      },
    [],
  );

  const getSearchableFilter = useCallback(
    (
      label: string,
      filterKey: string,
      filterName: string,
      getOptionsOnSearch: (searchString: string) => Promise<Option[]>,
      isMultiple = false,
      triggerTitle?: string,
    ) => ({
      value: filterKey,
      label,
      clearFields: [filterName],
      apply: getSearchableFilterApply(filterKey, filterName),
      getInitialValue: getSearchableFilterInitialValue(
        filterKey,
        filterName,
        isMultiple,
      ),
      render: ({ value, onChange }: HandleChangeSearchableFilter) => (
        <SearchableFilter
          value={value}
          filterKey={filterKey}
          triggerTitle={triggerTitle}
          isMultiple={isMultiple}
          onSelect={onChange}
          getOptionsOnSearch={getOptionsOnSearch}
        />
      ),
    }),
    [getSearchableFilterApply, getSearchableFilterInitialValue],
  );

  const getMultiSelectSearchableFilter = useCallback(
    (
      label: string,
      filterKey: string,
      filterName: string,
      getOptionsOnSearch: (searchString: string) => Promise<Option[]>,
      triggerTitle?: string,
      hasCheckboxAll = true,
    ) => ({
      value: filterKey,
      label,
      clearFields: [filterName],
      apply: getSearchableFilterApply(filterKey, filterName),
      getInitialValue: getSearchableFilterInitialValue(
        filterKey,
        filterName,
        true,
      ),
      render: ({ value, onChange }: HandleChangeSearchableFilter) => (
        <SearchableMultiSelectFilter
          value={value}
          filterKey={filterKey}
          triggerTitle={triggerTitle}
          hasCheckboxAll={hasCheckboxAll}
          onSelect={onChange}
          getOptionsOnSearch={getOptionsOnSearch}
        />
      ),
    }),
    [getSearchableFilterApply, getSearchableFilterInitialValue],
  );

  const getPriceRangeFilter = useCallback(
    (label: string, filterFrom: string, filterTo: string) => ({
      value: filterTo,
      label,
      clearFields: [filterFrom],
      render: ({
        value,
        onChange,
      }: {
        value: PriceRangeValue;
        onChange: (value: PriceRangeValue) => void;
      }) => (
        <PriceRangeFilter
          renderAsPortal={!isPhablet}
          title={t('selectAnOption')}
          showIcon={false}
          value={value}
          onApply={onChange}
        />
      ),
      apply: (value: PriceRangeValue) => {
        const from = value?.from;
        const to = value?.to;

        return {
          ...(from && { [filterFrom]: String(from) }),
          ...(to && { [filterTo]: String(to) }),
        };
      },
      getInitialValue: (activeFilter: Record<string, string>) => {
        const from = activeFilter[filterFrom];
        const to = activeFilter[filterTo];

        return {
          ...(from && { from: +from }),
          ...(to && { to: +to }),
        };
      },
    }),
    [isPhablet, t],
  );

  return {
    getSearchableFilter,
    getPriceRangeFilter,
    getMultiSelectSearchableFilter,
  };
};

export default useGetFilterOptions;
